/**
 * Supafish Delivery Location Selector
 *
 * Handles:
 *  - Interactive Leaflet map with click-to-pin
 *  - Geolocation / manual search (results pinned on map)
 *  - ERP API delivery check
 *  - localStorage caching
 *  - UI updates (header label, modal current location, product card warnings)
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'delivery_location';
  var API_ENDPOINT = 'https://supafish.winocle.com/api/check-delivery';
  var CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
  var HEADER_MAX_CHARS = 18;

  // Default map center (Kerala, India region — adjust to your default area)
  var DEFAULT_LAT = 10.8505;
  var DEFAULT_LNG = 76.2711;
  var DEFAULT_ZOOM = 12;

  /* ---- DOM refs ---- */
  var modal = document.getElementById('LocationModal');
  var geoBtn = document.getElementById('LocationModal-geoBtn');
  var searchInput = document.getElementById('LocationModal-search');
  var searchClear = document.getElementById('LocationModal-searchClear');
  var resultsList = document.getElementById('LocationModal-results');
  var statusEl = document.getElementById('LocationModal-status');
  var loadingEl = document.getElementById('LocationModal-loading');
  var resultEl = document.getElementById('LocationModal-result');
  var resultIcon = document.getElementById('LocationModal-resultIcon');
  var resultText = document.getElementById('LocationModal-resultText');
  var banner = document.getElementById('DeliveryBanner');
  var bannerText = document.getElementById('DeliveryBanner-text');
  var bannerClose = document.getElementById('DeliveryBanner-close');
  var headerLabel = document.querySelector('.header__location-label');
  var headerToggle = document.querySelector('.header__location-toggle');
  var currentSection = document.getElementById('LocationModal-current');
  var currentName = document.getElementById('LocationModal-currentName');
  var changeBtn = document.getElementById('LocationModal-changeBtn');
  var searchSection = document.getElementById('LocationModal-searchSection');

  // Map elements
  var mapEl = document.getElementById('LocationModal-map');
  var mapSection = document.getElementById('LocationModal-mapSection');
  var pinInfo = document.getElementById('LocationModal-pinInfo');
  var pinAddress = document.getElementById('LocationModal-pinAddress');
  var confirmPinBtn = document.getElementById('LocationModal-confirmPin');

  /* ---- State ---- */
  var isChecking = false;
  var activeIndex = -1;
  var map = null;
  var marker = null;
  var pinnedLat = null;
  var pinnedLng = null;
  var pinnedName = null;
  var mapInitialized = false;

  /* ---- Helpers ---- */
  function getStored() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (data.timestamp && Date.now() - data.timestamp > CACHE_DURATION_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function store(data) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.assign({}, data, { timestamp: Date.now() })));
  }

  function truncate(text, maxLen) {
    if (!text || text.length <= maxLen) return text;
    return text.substring(0, maxLen) + '..';
  }

  function showStatus(msg, type) {
    statusEl.textContent = msg;
    statusEl.className = 'location-modal__status location-modal__status--' + type;
    statusEl.hidden = false;
  }

  function hideStatus() {
    statusEl.hidden = true;
  }

  function showLoading() {
    loadingEl.hidden = false;
    resultEl.hidden = true;
    hideStatus();
  }

  function hideLoading() {
    loadingEl.hidden = true;
  }

  function showResult(success, message) {
    resultIcon.className = 'location-modal__result-icon location-modal__result-icon--' + (success ? 'success' : 'error');
    resultIcon.innerHTML = success
      ? '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>'
      : '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>';
    resultText.innerHTML = message;
    resultEl.hidden = false;
  }

  /* ---- Map ---- */
  function initMap() {
    if (mapInitialized || typeof L === 'undefined') return;

    map = L.map(mapEl, {
      center: [DEFAULT_LAT, DEFAULT_LNG],
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      attributionControl: false
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19
    }).addTo(map);

    map.on('click', function (e) {
      placePin(e.latlng.lat, e.latlng.lng);
    });

    mapInitialized = true;
  }

  function invalidateMapSize() {
    if (map) {
      // Must wait a tick for the dialog to finish its show animation
      setTimeout(function () { map.invalidateSize(); }, 150);
    }
  }

  function placePin(lat, lng) {
    pinnedLat = lat;
    pinnedLng = lng;

    if (marker) {
      marker.setLatLng([lat, lng]);
    } else {
      marker = L.marker([lat, lng], { draggable: true }).addTo(map);
      marker.on('dragend', function () {
        var pos = marker.getLatLng();
        pinnedLat = pos.lat;
        pinnedLng = pos.lng;
        reverseGeocode(pos.lat, pos.lng);
      });
    }

    map.setView([lat, lng], Math.max(map.getZoom(), 14), { animate: true });

    // Show confirm button, start reverse geocoding
    confirmPinBtn.hidden = false;
    pinInfo.hidden = false;
    pinAddress.textContent = window.locationStrings.reverseGeocoding;

    reverseGeocode(lat, lng);
  }

  function reverseGeocode(lat, lng) {
    fetch(
      'https://nominatim.openstreetmap.org/reverse?lat=' +
        lat + '&lon=' + lng +
        '&format=json&zoom=16&addressdetails=1'
    )
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.display_name) {
          pinnedName = data.display_name.split(',').slice(0, 2).join(',').trim();
          pinAddress.textContent = pinnedName;
        } else {
          pinnedName = lat.toFixed(4) + ', ' + lng.toFixed(4);
          pinAddress.textContent = pinnedName;
        }
      })
      .catch(function () {
        pinnedName = lat.toFixed(4) + ', ' + lng.toFixed(4);
        pinAddress.textContent = pinnedName;
      });
  }

  function resetMapState() {
    if (marker) {
      map.removeLayer(marker);
      marker = null;
    }
    pinnedLat = null;
    pinnedLng = null;
    pinnedName = null;
    confirmPinBtn.hidden = true;
    pinInfo.hidden = true;
    pinAddress.textContent = '';
  }

  /* ---- API ---- */
  async function checkDelivery(lat, lng) {
    var res = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lat: lat, lng: lng }),
    });
    if (!res.ok) throw new Error('API error ' + res.status);
    return res.json();
  }

  /* ---- Core logic ---- */
  async function verifyLocation(lat, lng, locationName) {
    if (isChecking) return;
    isChecking = true;

    // Determine the display name upfront
    var zoneName = locationName || pinnedName || (searchInput.value ? searchInput.value.split(',')[0].trim() : '') || window.locationStrings.yourArea;

    // Close modal immediately and update header optimistically
    closeModal();
    updateHeader(zoneName);

    try {
      var data = await checkDelivery(lat, lng);
      var deliverable = !!data.deliverable;
      // Prefer server zone name, fall back to what we already have
      var finalZone = data.zone_name || data.area || zoneName;

      store({ lat: lat, lng: lng, deliverable: deliverable, zone_name: finalZone });

      if (deliverable) {
        hideBanner();
        enableAddToCart(true);
      } else {
        showBanner();
        enableAddToCart(false);
      }

      // Always show the confirmed zone name in the header
      updateHeader(finalZone, deliverable);
    } catch (err) {
      console.error('Delivery check failed:', err);
      // On API error, allow add-to-cart as a safe fallback
      store({ lat: lat, lng: lng, deliverable: true, zone_name: zoneName });
      updateHeader(zoneName);
      hideBanner();
      enableAddToCart(true);
    } finally {
      isChecking = false;
    }
  }

  /* ---- Geolocation ---- */
  function useCurrentLocation() {
    if (!navigator.geolocation) {
      showStatus(window.locationStrings.geoUnsupported, 'error');
      return;
    }

    showStatus(window.locationStrings.geoPrompt, 'info');

    navigator.geolocation.getCurrentPosition(
      function (pos) {
        hideStatus();
        placePin(pos.coords.latitude, pos.coords.longitude);
      },
      function (err) {
        var msg = window.locationStrings.geoError;
        if (err.code === 1) msg = window.locationStrings.geoDenied;
        if (err.code === 2) msg = window.locationStrings.geoUnavailable;
        if (err.code === 3) msg = window.locationStrings.geoTimeout;
        showStatus(msg, 'error');
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  /* ---- Manual search ---- */
  var searchTimeout = null;

  function clearSearchResults() {
    resultsList.innerHTML = '';
    resultsList.hidden = true;
    activeIndex = -1;
  }

  function renderSearchResults(places) {
    clearSearchResults();
    if (!places.length) {
      resultsList.innerHTML = '<li class="location-modal__results-empty">' + window.locationStrings.noResults + '</li>';
      resultsList.hidden = false;
      return;
    }

    places.forEach(function (place) {
      var li = document.createElement('li');
      li.className = 'location-modal__results-item';
      li.setAttribute('role', 'option');
      li.dataset.lat = place.lat;
      li.dataset.lon = place.lon;
      li.innerHTML =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>' +
        '<span>' + place.display_name + '</span>';
      li.addEventListener('click', function () { selectSearchResult(place); });
      resultsList.appendChild(li);
    });

    resultsList.hidden = false;
  }

  function selectSearchResult(place) {
    clearSearchResults();
    var shortName = place.display_name.split(',')[0];
    searchInput.value = place.display_name.split(',').slice(0, 2).join(',').trim();
    searchClear.hidden = false;

    // Place pin on map instead of immediately verifying
    var lat = parseFloat(place.lat);
    var lng = parseFloat(place.lon);
    placePin(lat, lng);
    pinnedName = shortName;
    pinAddress.textContent = searchInput.value;
  }

  function setActiveResult(index) {
    var items = resultsList.querySelectorAll('.location-modal__results-item');
    items.forEach(function (el, i) { el.classList.toggle('location-modal__results-item--active', i === index); });
    activeIndex = index;
  }

  function handleSearch() {
    var query = searchInput.value.trim();
    searchClear.hidden = !query;

    if (!query || query.length < 3) {
      clearSearchResults();
      return;
    }

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(function () {
      fetch(
        'https://nominatim.openstreetmap.org/search?q=' +
          encodeURIComponent(query) +
          '&format=json&limit=5&addressdetails=1'
      )
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data && data.length > 0) {
            renderSearchResults(data);
          } else {
            renderSearchResults([]);
          }
        })
        .catch(function () {
          clearSearchResults();
          showStatus(window.locationStrings.searchError, 'error');
        });
    }, 400);
  }

  function handleSearchKeydown(e) {
    var items = resultsList.querySelectorAll('.location-modal__results-item');
    if (!items.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      var next = Math.min(activeIndex + 1, items.length - 1);
      setActiveResult(next);
      items[next].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var prev = Math.max(activeIndex - 1, 0);
      setActiveResult(prev);
      items[prev].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && activeIndex >= 0 && items[activeIndex]) {
      e.preventDefault();
      items[activeIndex].click();
    } else if (e.key === 'Escape') {
      clearSearchResults();
    }
  }

  /* ---- UI updates ---- */
  function updateHeader(zoneName, deliverable) {
    if (!headerLabel) return;
    if (zoneName) {
      headerLabel.textContent = truncate(zoneName, HEADER_MAX_CHARS);
      headerLabel.title = zoneName;
      headerLabel.dataset.deliveryStatus = deliverable === false ? 'undeliverable' : 'deliverable';
    } else {
      // No location chosen yet — neutral prompt, not an error state
      headerLabel.textContent = window.locationStrings.selectLocation;
      headerLabel.title = '';
      headerLabel.dataset.deliveryStatus = 'unset';
    }
  }

  function showBanner() {
    if (banner) banner.hidden = false;
    document.body.classList.add('delivery-blocked');
  }

  function hideBanner() {
    if (banner) banner.hidden = true;
    document.body.classList.remove('delivery-blocked');
  }

  function enableAddToCart(enabled) {
    document.body.classList.toggle('delivery-blocked', !enabled);

    var cached = getStored();
    var hasLocation = !!cached;

    // Remove existing delivery warnings from product cards
    document.querySelectorAll('.product-card__delivery-warning').forEach(function (el) { el.remove(); });

    if (!enabled) {
      // --- Product page buttons: change text, don't disable (click interception handles it) ---
      document.querySelectorAll('.product-info__add-to-cart, [name="add"]').forEach(function (btn) {
        if (btn.closest('.product-card')) return;
        if (!btn.dataset.originalHtml) btn.dataset.originalHtml = btn.innerHTML;
        btn.setAttribute('data-delivery-blocked', 'true');

        var label = hasLocation
          ? window.locationStrings.unserviceableBtn
          : window.locationStrings.unserviceableSelect;
        btn.innerHTML =
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg> ' +
          label;
      });

      // --- Product cards: add warning bar at absolute bottom, don't touch the button ---
      document.querySelectorAll('.product-card').forEach(function (card) {
        if (!card.querySelector('.product-card__delivery-warning')) {
          var warning = document.createElement('div');
          warning.className = 'product-card__delivery-warning';
          warning.textContent = hasLocation
            ? window.locationStrings.cartWarning
            : window.locationStrings.unserviceableSelect;
          card.appendChild(warning);
        }
      });
    } else {
      // --- Restore product page buttons ---
      document.querySelectorAll('[data-delivery-blocked]').forEach(function (btn) {
        btn.removeAttribute('data-delivery-blocked');
        if (btn.dataset.originalHtml) {
          btn.innerHTML = btn.dataset.originalHtml;
          delete btn.dataset.originalHtml;
        }
      });
    }

    document.dispatchEvent(
      new CustomEvent('delivery:status-changed', { detail: { deliverable: enabled } })
    );
  }

  /* ---- Modal open/close ---- */
  function openModal() {
    if (!modal) return;

    hideStatus();
    hideLoading();
    resultEl.hidden = true;
    searchInput.value = '';
    if (searchClear) searchClear.hidden = true;
    clearSearchResults();

    // Reset map state for fresh selection
    resetMapState();

    // Show current location section if one is saved
    var cached = getStored();
    if (cached && cached.zone_name) {
      currentName.textContent = cached.zone_name;
      currentSection.hidden = false;
      if (searchSection) searchSection.style.display = 'none';
      if (mapSection) mapSection.style.display = 'none';
    } else {
      currentSection.hidden = true;
      if (searchSection) searchSection.style.display = '';
      if (mapSection) mapSection.style.display = '';
    }

    modal.showModal();

    // Init map lazily on first open
    initMap();
    invalidateMapSize();

    // If there's a cached location, center map there
    if (cached && cached.lat && cached.lng) {
      map.setView([cached.lat, cached.lng], DEFAULT_ZOOM);
    }
  }

  function closeModal() {
    if (!modal) return;
    modal.close();
  }

  /* ---- Init ---- */
  function init() {
    // Button events
    if (geoBtn) geoBtn.addEventListener('click', useCurrentLocation);
    if (searchInput) {
      searchInput.addEventListener('input', handleSearch);
      searchInput.addEventListener('keydown', handleSearchKeydown);
    }
    if (searchClear) {
      searchClear.addEventListener('click', function () {
        searchInput.value = '';
        searchClear.hidden = true;
        clearSearchResults();
        searchInput.focus();
      });
    }

    // Confirm pin button
    if (confirmPinBtn) {
      confirmPinBtn.addEventListener('click', function () {
        if (pinnedLat !== null && pinnedLng !== null) {
          var name = pinnedName || (searchInput.value ? searchInput.value.split(',')[0].trim() : '');
          verifyLocation(pinnedLat, pinnedLng, name);
        }
      });
    }

    // Change location button in modal
    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        currentSection.hidden = true;
        if (searchSection) searchSection.style.display = '';
        if (mapSection) mapSection.style.display = '';
        searchInput.focus();
        invalidateMapSize();

        // Place a pin at the currently saved location so the user sees it
        var cached = getStored();
        if (cached && cached.lat && cached.lng) {
          setTimeout(function () { placePin(cached.lat, cached.lng); }, 200);
        }
      });
    }

    // Close search results on outside click
    document.addEventListener('click', function (e) {
      if (!e.target.closest('.location-modal__search')) {
        clearSearchResults();
      }
    });

    // Close modal events
    document.querySelectorAll('[data-location-close]').forEach(function (el) {
      el.addEventListener('click', closeModal);
    });

    // Open modal from header
    if (headerToggle) {
      headerToggle.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openModal();
      });
    }

    if (modal) {
      modal.addEventListener('close', function () {
        hideStatus();
        hideLoading();
      });
    }

    if (bannerClose) {
      bannerClose.addEventListener('click', hideBanner);
    }

    // Intercept add-to-cart clicks when delivery is blocked
    document.addEventListener('click', function (e) {
      var addBtn = e.target.closest(
        '.product-info__add-to-cart, .product-card__add-btn, [name="add"], .product-form__submit, .product-card__delivery-warning'
      );
      if (!addBtn) return;
      if (document.body.classList.contains('delivery-blocked')) {
        e.preventDefault();
        e.stopPropagation();
        openModal();
      }
    }, true);

    // Intercept form submissions when delivery is blocked
    document.addEventListener('submit', function (e) {
      if (document.body.classList.contains('delivery-blocked')) {
        if (e.target.querySelector('[name="add"]')) {
          e.preventDefault();
          e.stopPropagation();
          openModal();
        }
      }
    }, true);

    // Block checkout
    document.addEventListener('click', function (e) {
      var checkoutBtn = e.target.closest('[name="checkout"], .cart__checkout');
      if (!checkoutBtn) return;
      if (document.body.classList.contains('delivery-blocked')) {
        e.preventDefault();
        e.stopPropagation();
        openModal();
      }
    }, true);

    // Restore from cache
    var cached = getStored();
    if (cached) {
      // Always show the cached zone name in the header
      updateHeader(cached.zone_name, cached.deliverable);
      if (cached.deliverable) {
        hideBanner();
        enableAddToCart(true);
      } else {
        showBanner();
        enableAddToCart(false);
      }
    } else {
      updateHeader(null);
      enableAddToCart(false);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
